import { useCallback, useMemo } from 'react';
import ProjectMember from '@projectMembers/models/projectMember';
import ProjectInvitation from '@projectMembers/models/projectInvitation';
import Project from 'src/entities/project';
import ProjectRole, { VALID_ASSIGNABLE_ROLES } from '../../models/projectRole';
import useFetchProjectMembersQuery from '../../queries/useFetchProjectMembersQuery';
import useFetchProjectPendingInvitationsQuery from '../../queries/useFetchProjectPendingInvitationsQuery';
import useProjectInvitationActions from './useProjectInvitationActions';
import useDeleteProjectMember from './useDeleteProjectMember';
import useUpdateProjectMemberRole from './useUpdateProjectMemberRole';

type UseProjectMembersListProps = {
	project: Project;
	currentUserRole: ProjectRole;
};

const useProjectMembersList = ( { project, currentUserRole }: UseProjectMembersListProps ) => {
	const { data: projectMembers = [] } = useFetchProjectMembersQuery( { projectID: project.id } );
	const { data: projectInvitations = [] } = useFetchProjectPendingInvitationsQuery( { projectID: project.id } );
	const onDeleteProjectMember = useDeleteProjectMember( { project } );
	const onUpdateProjectMemberRole = useUpdateProjectMemberRole( { project } );
	const { onDeleteProjectInvitation } = useProjectInvitationActions( { projectID: project.id } );

	const roleOptions = VALID_ASSIGNABLE_ROLES.map( role => ( {
		role,
		disabled: !currentUserRole.canInviteRole( role ),
		additionalInfo: !currentUserRole.canInviteRole( role ) ? 'Only Managers' : undefined
	} ) );

	const members = useMemo( () => {
		const owner = projectMembers.find( member => member.isOwner );
		const sortedMembers = projectMembers
			.filter( member => !member.isOwner )
			.sort( ( a, b ) => {
				const roleLevelDiff = a.role.level - b.role.level;
				return roleLevelDiff !== 0 ? roleLevelDiff : a.user.name.localeCompare( b.user.name );
			} );

		return owner ? [ owner, ...sortedMembers ] : sortedMembers;
	}, [ projectMembers ] );

	const invitations = useMemo( () => (
		[ ...projectInvitations ]
			.sort( ( a, b ) => {
				const roleLevelDiff = a.role.level - b.role.level;
				return roleLevelDiff !== 0 ? roleLevelDiff : a.email.localeCompare( b.email );
			} )
	), [ projectInvitations ] );

	const canEditProjectMember = useCallback( ( member: ProjectMember ) => (
		currentUserRole.canEditProjectMemberRole( member )
	), [ currentUserRole ] );

	const canDeleteProjectMember = useCallback( ( member: ProjectMember ) => (
		currentUserRole.canDeleteProjectMember( member )
	), [ currentUserRole ] );

	const canDeleteProjectInvitation = useCallback( ( invitation: ProjectInvitation ) => (
		currentUserRole.canDeleteProjectInvite( invitation )
	), [ currentUserRole ] );

	return {
		members,
		invitations,
		roleOptions,
		canEditProjectMember,
		canDeleteProjectMember,
		canDeleteProjectInvitation,
		onDeleteProjectMember,
		onUpdateProjectMemberRole,
		onDeleteProjectInvitation
	};
};

export default useProjectMembersList;
